import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import ScreeningFlow from '../screening/ScreeningFlow';

function Screening() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isID = i18n.language === 'id';

  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [history, setHistory] = useState([]);

  const [openId, setOpenId] = useState(null); // id riwayat yang sedang dibuka

  // ambil daftar anak
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('children').select('*').eq('caregiver_id', user.id);
      if (active && data && data.length > 0) {
        setChildren(data);
        setSelectedChild(data[0].id);
      }
    })();
    return () => { active = false; };
  }, [user.id]);

  // ambil riwayat (dipakai juga setelah simpan)
  const fetchHistory = useCallback(async () => {
    const { data } = await supabase
      .from('screenings')
      .select('*, children(name)')
      .eq('caregiver_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);
    if (data) setHistory(data);
  }, [user.id]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // INI inti Opsi B: ScreeningFlow memanggil onComplete saat selesai,
  // di sinilah penyimpanan terjadi.
  const handleComplete = async (scores, answers) => {
    const { giScore, gsssScore, flaccScore } = scores;
    const { error } = await supabase.from('screenings').insert([{
      caregiver_id: user.id,
      child_id: selectedChild,
      answers: answers,                          // ← simpan jawaban lengkap (JSON)
      score: giScore + gsssScore + flaccScore,
    }]);
    if (error) {
      console.error('Gagal menyimpan skrining:', error.message);
    } else {
      fetchHistory();
    }
  };

  return (
    <div>
      <ScreeningFlow
        mode="auth"
        childrenList={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
        onComplete={handleComplete}
        onRegister={() => navigate('/register')}
        onLogin={() => navigate('/login')}
      />

      {/* Riwayat skrining (khusus mode login) */}
      {history.length > 0 && (
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 20px 48px' }}>
          <h2 style={{
            fontSize: 'var(--text-h3)', fontWeight: 700,
            color: 'var(--ink)', marginBottom: 14,
          }}>
            {isID ? 'Riwayat Skrining' : 'Screening History'}
          </h2>
          {history.map(h => {
            const isOpen = openId === h.id;
            return (
              <div key={h.id} style={{
                background: 'var(--canvas)', border: '1px solid var(--teal-light)',
                borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: 10,
              }}>
                <button
                  onClick={() => setOpenId(isOpen ? null : h.id)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left', padding: 0,
                  }}
                  aria-expanded={isOpen}
                >
                  <div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--body)' }}>
                      {new Date(h.created_at).toLocaleDateString(isID ? 'id-ID' : 'en-US')}
                    </div>
                    {h.children && (
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)' }}>
                        {h.children.name}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--teal-primary)' }}>
                      Total: {h.score}
                    </span>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--teal-primary)' }}>
                      {isOpen ? (isID ? 'Tutup ▲' : 'Close ▲') : (isID ? 'Lihat ▼' : 'View ▼')}
                    </span>
                  </div>
                </button>

                {isOpen && <ScreeningDetail screening={h} isID={isID} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Menampilkan detail jawaban satu skrining (dibaca dari kolom answers).
// Aman untuk skrining lama: kalau answers kosong, hanya tampilkan skor total.
function ScreeningDetail({ screening, isID }) {
  const a = screening.answers;

  if (!a || (!a.gi && !a.gsss && !a.flacc)) {
    return (
      <div style={detailStyles.empty}>
        {isID
          ? 'Rincian jawaban tidak tersedia untuk skrining ini (dibuat sebelum fitur ini ada). Total skor: '
          : 'Answer details are unavailable for this screening (created before this feature). Total score: '}
        <strong>{screening.score ?? '-'}</strong>
      </div>
    );
  }

  const sections = [
    { key: 'gi', title: isID ? 'Skrining Awal' : 'Initial Screening', max: 12 },
    { key: 'gsss', title: isID ? 'Tingkat Keparahan Gejala Pencernaan' : 'GI Symptom Severity', max: 21 },
    { key: 'flacc', title: isID ? 'Tingkat Nyeri & Ketidaknyamanan' : 'Pain & Discomfort Level', max: 10 },
  ];

  return (
    <div style={detailStyles.wrap}>
      {/* skor ringkas */}
      <div style={detailStyles.scoreRow}>
        {sections.map(s => (
          <div key={s.key} style={detailStyles.scoreBox}>
            <div style={detailStyles.scoreLabel}>{s.title}</div>
            <div style={detailStyles.scoreValue}>
              {a.scores?.[s.key] ?? '-'}<span style={detailStyles.scoreMax}>/{s.max}</span>
            </div>
          </div>
        ))}
      </div>

      {/* daftar jawaban per instrumen */}
      {sections.map(s => (
        Array.isArray(a[s.key]) && a[s.key].length > 0 && (
          <div key={s.key} style={detailStyles.group}>
            <div style={detailStyles.groupTitle}>{s.title}</div>
            {a[s.key].map((item, i) => (
              <div key={i} style={detailStyles.qaRow}>
                <span style={detailStyles.qaQuestion}>{item.q}</span>
                <span style={detailStyles.qaAnswer}>{item.a}</span>
              </div>
            ))}
          </div>
        )
      ))}
    </div>
  );
}

const detailStyles = {
  wrap: { marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--teal-light)' },
  empty: { fontSize: 'var(--text-sm)', color: 'var(--body)', lineHeight: 1.6 },
  scoreRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 },
  scoreBox: { background: 'var(--canvas-soft)', border: '1px solid var(--teal-light)', borderRadius: 'var(--radius-sm)', padding: '10px 8px', textAlign: 'center' },
  scoreLabel: { fontSize: 11, color: 'var(--body-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  scoreValue: { fontSize: 'var(--text-lg)', fontWeight: 800, color: 'var(--teal-primary)' },
  scoreMax: { fontSize: 12, color: 'var(--body-mid)', fontWeight: 400 },
  group: { marginBottom: 16 },
  groupTitle: { fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--teal-primary)', marginBottom: 8 },
  qaRow: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--teal-light)' },
  qaQuestion: { fontSize: 'var(--text-sm)', color: 'var(--ink-mid)', lineHeight: 1.5, flex: 1 },
  qaAnswer: { fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', flexShrink: 0 },
};

export default Screening;